import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LocalStorageConstants } from '@shared/constants/local-storage-constants';
import { AuthService } from '@shared/helpers/auth.service';
import { SettingsState } from '@shared/states/settings.state';
import { CommonErrorData } from '@shared/types/error-responses';
import { withStoredToken } from './token.interceptor';

/**
 * Description of the gateway's 401 for a token whose permissions version (`pv` claim) is no longer
 * the user's: an admin changed their role, profile or ACLs after it was issued (.project#2483).
 * The cookie refresh still works and issues a token with the current version.
 */
export const JWT_PERMISSIONS_CHANGED_DESCRIPTION = 'JWT permissions changed';

/**
 * Answers the gateway's "JWT permissions changed" 401 by renewing the token once and sending the
 * request again with it, so an admin's edit does not leave the user with refused requests until the
 * token's expiry. Every other response, and a renewal that is refused, passes on unchanged.
 *
 * Registered last, closest to the backend: the error toasts and the widget cache see only the
 * outcome of the retry, and the retry goes straight to the backend, so it can never come back here.
 */
@Injectable()
export class PermissionsChangedInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private settingsState: SettingsState,
  ) {}

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (this.isSessionEndpoint(request)) {
      return next.handle(request);
    }

    return next
      .handle(request)
      .pipe(
        catchError((response: unknown) =>
          isPermissionsChanged(response).pipe(
            switchMap((permissionsChanged) =>
              permissionsChanged
                ? this.renewAndRetry(request, next, response)
                : throwError(() => response),
            ),
          ),
        ),
      );
  }

  private renewAndRetry(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    refusal: unknown,
  ): Observable<HttpEvent<unknown>> {
    const sentToken = request.headers.get(LocalStorageConstants.TokenHeader);
    const storedToken = localStorage.getItem(LocalStorageConstants.Token);
    // A request answered after another one has already renewed the token only needs the new one.
    const renewed$ =
      storedToken && storedToken !== sentToken ? of(true) : this.authService.renewRefusedToken();

    return renewed$.pipe(
      switchMap((renewed) =>
        renewed ? next.handle(withStoredToken(request)) : throwError(() => refusal),
      ),
    );
  }

  /** The login, refresh and logout calls: a refusal there is not something a refresh can fix. */
  private isSessionEndpoint(request: HttpRequest<unknown>): boolean {
    const apiPath = this.settingsState.apiPath;

    return (
      request.url.startsWith(`${apiPath}/auth/`) || request.url === `${apiPath}/session-logout`
    );
  }
}

function isPermissionsChanged(response: unknown): Observable<boolean> {
  if (!(response instanceof HttpErrorResponse) || response.status !== 401) {
    return of(false);
  }

  const body: unknown = response.error;

  // A download (`responseType: 'blob'`) receives the JSON error body as a Blob.
  if (body instanceof Blob) {
    return from(body.text()).pipe(
      map(hasPermissionsChangedDescription),
      catchError(() => of(false)),
    );
  }

  return of(hasPermissionsChangedDescription(body));
}

function hasPermissionsChangedDescription(body: unknown): boolean {
  let error = body;

  if (typeof error === 'string') {
    try {
      error = JSON.parse(error);
    } catch {
      return false;
    }
  }

  return (error as CommonErrorData | null)?.description === JWT_PERMISSIONS_CHANGED_DESCRIPTION;
}
